import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useTranslation} from '@/translations/i18n'
import {useFieldArray, useFormContext} from 'react-hook-form'
import {z} from 'zod'
import {StyledEditProjectTeamFormFieldsGrid} from '@/features/projects/components/project-form/edit-project-team-form-fields/style'
import {useMemo} from 'react'
import {EditProjectTeamOperatorField} from '@/features/projects/components/project-form/edit-project-team-operator-field/EditProjectTeamOperatorField'
import {EditProjectTeamGroupField} from '@/features/projects/components/project-form/edit-project-team-group-field/EditProjectTeamGroupField'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'

const EditProjectTeamFormSchema = ProjectFieldsSchema.pick({
    team: true
})
type EditProjectTeamFormSchema = z.infer<typeof EditProjectTeamFormSchema>

export const EditProjectTeamFormFields = ({isAReserve}: {isAReserve?: boolean}) => {
    const {t} = useTranslation()
    const {control} = useFormContext<EditProjectTeamFormSchema>()

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'team',
        keyName: 'fieldId'
    })

    const disabledOperatorsOptions = useMemo(() => {
        return fields.map(field => field.id)
    }, [fields])

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            {fields.length > 0 ? (
                <>
                    {fields.map((field, index) => {
                        return (
                            <StyledEditProjectTeamFormFieldsGrid key={field.fieldId}>
                                <GridItem spanColumn={1}>
                                    <EditProjectTeamGroupField isAReserve={isAReserve} index={index} />
                                </GridItem>
                                <GridItem spanColumn={1}>
                                    <EditProjectTeamOperatorField
                                        index={index}
                                        disabledOptions={disabledOperatorsOptions}
                                        isAReserve={isAReserve}
                                    />
                                </GridItem>
                                <GridItem spanColumn={1}>
                                    {fields.length > 1 && (
                                        <Button
                                            variant="ghost"
                                            onClick={() => remove(index)}
                                            style={{marginTop: '28px'}}
                                        >
                                            <Trash01Icon />
                                        </Button>
                                    )}
                                </GridItem>
                            </StyledEditProjectTeamFormFieldsGrid>
                        )
                    })}
                </>
            ) : (
                <EmptyState variant="secondary" title={t('projects:no_team_members')} />
            )}

            <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                    append({
                        id: '',
                        fullName: '',
                        group: fields.length == 0 && !isAReserve ? 'referring_doctor' : 'therapist',
                        specializationName: ''
                    })
                }
            >
                <PlusIcon />
                {t('projects:add_operator')}
            </Button>
        </Flexbox>
    )
}
